// src/auditoria/utils/tx-modal-provider.tsx

import type { ReactNode } from 'react';
import type { DialogProps } from '@mui/material';

import { v4 as uuidv4 } from 'uuid';
import { useRef, useState, useContext, useCallback, createContext } from 'react';

import { TxSimpleModal } from 'src/auditoria/components/tx-simple-modal/tx-simple-modal';

// Tipagem de cada modal
type TxModalEntry = {
  open: boolean;
  title: ReactNode;
  content: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  dialogProps?: Partial<DialogProps>;
};

// Tipagem do contexto
type TxModalContextType = {
  /**
   * `aoFechar` roda quando o modal fecha, de qualquer jeito (botão, backdrop ou
   * `fecharModalAtual`). Serve pra tela que abriu o modal recarregar o que
   * mudou lá dentro — sem isso ela só descobre no F5.
   */
  abrirModal: (
    content: ReactNode,
    title?: ReactNode,
    maxWidth?: TxModalEntry['maxWidth'],
    dialogProps?: Partial<DialogProps>,
    aoFechar?: () => void
  ) => string;
  fecharModal: (id: string) => void;
};

const TxModalContext = createContext<TxModalContextType | null>(null);

// Variáveis globais
let abrirModalGlobalFn: TxModalContextType['abrirModal'] | null = null;
let fecharModalGlobalFn: TxModalContextType['fecharModal'] | null = null;
let lastModalIdRef: { current: string | null } = { current: null };

export function TxModalProvider({ children }: { children: ReactNode }) {
  const [modais, setModais] = useState<Record<string, TxModalEntry>>({});

  // Guarda o último ID aqui
  const lastModalId = useRef<string | null>(null);

  // Callbacks de fechamento ficam FORA do state: o updater do setState pode ser
  // reexecutado (StrictMode) e efeito colateral lá dentro dispararia duas vezes.
  const aoFecharRef = useRef<Record<string, () => void>>({});

  const abrirModal = useCallback(
    (
      content: ReactNode,
      title: ReactNode = 'Modal',
      maxWidth = 'sm',
      dialogProps?: Partial<DialogProps>,
      aoFechar?: () => void
    ) => {
      const id = uuidv4();
      lastModalId.current = id;
      if (aoFechar) aoFecharRef.current[id] = aoFechar;
      setModais((prev) => ({
        ...prev,
        [id]: {
          content,
          title,
          maxWidth: maxWidth as TxModalEntry['maxWidth'],
          open: true,
          dialogProps,
        },
      }));
      return id;
    },
    []
  );

  const fecharModal = useCallback((id: string) => {
    setModais((prev) => ({
      ...prev,
      [id]: { ...prev[id], open: false },
    }));
    const aoFechar = aoFecharRef.current[id];
    delete aoFecharRef.current[id];
    aoFechar?.();
  }, []);

  // registra as globais
  abrirModalGlobalFn = abrirModal;
  fecharModalGlobalFn = fecharModal;
  lastModalIdRef = lastModalId;

  return (
    <TxModalContext.Provider value={{ abrirModal, fecharModal }}>
      {children}
      {Object.entries(modais).map(
        ([id, modal]) =>
          modal.open && (
            <TxSimpleModal
              key={id}
              open={modal.open}
              onClose={() => fecharModal(id)}
              title={modal.title}
              content={modal.content}
              maxWidth={modal.maxWidth}
              dialogProps={modal.dialogProps}
            />
          )
      )}
    </TxModalContext.Provider>
  );
}

export function useGlobalTxModal() {
  const context = useContext(TxModalContext);
  if (!context) {
    throw new Error('useGlobalTxModal deve ser usado dentro de <TxModalProvider>');
  }
  return context;
}

export function abrirModalGlobal(
  content: ReactNode,
  title?: ReactNode,
  maxWidth?: TxModalEntry['maxWidth'],
  dialogProps?: Partial<DialogProps>,
  aoFechar?: () => void
): string {
  if (!abrirModalGlobalFn) {
    throw new Error('abrirModalGlobal só pode ser usado após <TxModalProvider> estar montado');
  }
  return abrirModalGlobalFn(content, title, maxWidth, dialogProps, aoFechar);
}

/**
 * Fecha o último modal aberto sem parâmetros
 */
export function fecharModalAtual(): void {
  if (!fecharModalGlobalFn) return;
  const id = lastModalIdRef.current;
  if (!id) return;
  fecharModalGlobalFn(id);
  lastModalIdRef.current = null;
}
